import { Injectable } from '@angular/core';
import { BehaviorSubject, of, from, Subject } from 'rxjs';
import { catchError, concatMap, map, switchMap, timeout } from 'rxjs/operators';
import { sidebarStaticMenu } from '../configs/application-menu';
import { BuildForm } from '../models/formulir/build-form.model';
import { Karyawan } from '../models/pegawai/karyawan.model';
import { AuthService } from './auth.service';
import { BuildFormDataService } from './data/formulir/build-form.data.service';
import { DetailNavigasiDataService } from './data/master-user/detail-navigasi.data.service';
import { IdbStorageService } from './storage/idb-storage.service';
import { storageDict } from './storage/storage-dict';
import { ClinicEnvironmentService } from './clinic-environment.service';
import { ReferenceNavigasiDataService } from './data/master-user/reference-navigasi.data.service';
import { compact, groupBy, orderBy, sortBy, uniq } from 'lodash-es';
import { MenuChild, MenuGroup, MenuParent } from '../models/application-menu.model';
import { sidebarStaticMenuCustomAdmin } from '../configs/application-menu-custom-admin';
import ValueGuard from '../utils/value-guard';
import { SettingAuthSatuSehatDataService } from 'src/app/shared/services/data/go-service/settings-service/setting-auth-satusehat.data.service';
import { SettingMonitorOutpatientQueueDataService } from './data/antrian/setting-monitor-outpatient-queue.data.service';
import { SettingMonitorOutpatientQueue } from '../models/antrian/setting-monitor-outpatient-queue.model';
import { SettingResepDataService } from './data/setting-resep.data.service';

@Injectable({
  providedIn: 'root',
})
export class SidebarService {
  private activeSidebar: MenuGroup[] = [];
  private karyawan: Karyawan;
  private isInitialized = false;
  public isLoading$ = new BehaviorSubject<boolean>(true);
  public sidebarUpdate$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private buildFormDataService: BuildFormDataService,
    private clinicEnv: ClinicEnvironmentService,
    private detailNavigasiDataService: DetailNavigasiDataService,
    private settingAuthSatuSehatDataService: SettingAuthSatuSehatDataService,
    private storageService: IdbStorageService,
    private referenceNavigasiDataService: ReferenceNavigasiDataService,
    private settingMonitorOutpatientQueueDataService: SettingMonitorOutpatientQueueDataService,
    private settingResepDataService: SettingResepDataService
  ) {
    // this.initSidebar();
    this.initAuthListener();
  }

  private async loadStaffMenu(initialGroup?: MenuGroup[]) {
    let result: MenuGroup[] = [
      {
        name: '',
        global: true,
        groupChildren: [
          {
            name: 'Dashboard',
            link: '/dashboard',
            icon: './assets/media/svg/icons/Periksa/beranda-icon.svg',
            single: true,
          },
        ],
      },
    ];

    try {
      const idLogin = await this.storageService.getItem(storageDict.idLogin);
      const navDetails = await this.detailNavigasiDataService
        .getByLogin(idLogin)
        .pipe(timeout(10000))
        .toPromise();
      const navs = navDetails.map((navItem) => navItem.referenceNavigasi);
      let navHeaders = Array.from(new Set(navs.map((res) => res.navigasi.id))).map((id) => {
        return navs.find((s) => s.navigasi.id === id).navigasi;
      });
      let navGroups = Array.from(new Set(navs.map((res) => res.navigasi.group))).map((group) => {
        const findNav = navs.find((s) => s.navigasi.group === group);
        if (group === null) {
          return { name: null, urutanGroup: 999 };
        }
        return { name: group, urutanGroup: findNav.navigasi.urutanGroup };
      });

      navHeaders = sortBy(navHeaders, 'urutan');
      navGroups = sortBy(navGroups, 'urutanGroup');

      /**
       * Array of module name that user can access.
       * It derived from db and will be compared in a route guard using URL path.
       */
      let moduleAccess: string[] = [];

      const navResult = navGroups
        .map((group) => {
          const appMenu: MenuParent[] = navHeaders
            .filter((header) => {
              return header.group === group.name;
            })
            .map((header) => {
              const navChildren = navs.filter((nav) => nav.navigasi.id === header.id);
              moduleAccess.push(...navChildren.map((c) => c.module));
              if (navChildren.every((item) => item.url === null || item.url.length < 1)) {
                return;
              }
              let availableChildren = navChildren.filter((item) => item.url !== null);

              let targetIcon = '';
              if (header.iconSuperApp) {
                targetIcon = header.iconSuperApp.includes('assets/')
                  ? header.iconSuperApp
                  : `/assets/media/svg/icons/Periksa/${header.iconSuperApp}`;
              }

              availableChildren = orderBy(availableChildren, (x) =>
                x.nama ? x.nama.toLowerCase() : ''
              );
              availableChildren = orderBy(availableChildren, (x) => x.urutan);

              return {
                name: header.nama,
                icon: targetIcon,
                children: availableChildren.map((child) => {
                  return {
                    name: child.nama,
                    link: `${child.url}`,
                    newTab: child.newTab,
                  } as MenuChild;
                }),
              };
            })
            .filter((item) => !!item);

          return {
            name: group.name || 'Lainnya',
            groupChildren: appMenu,
          } as MenuGroup;
        })
        .filter((group) => group.groupChildren.length > 0);

      navResult.forEach((nav) => {
        const sameGroup = result.find((group) => group.name === nav.name);
        if (ValueGuard.againtsNullOrUndefined(sameGroup)) {
          sameGroup.groupChildren.push(...nav.groupChildren);
        } else {
          result = [...result, nav];
        }
      });

      if (moduleAccess.length > 0) {
        moduleAccess = [...uniq(moduleAccess)];
        await this.storageService.setItem(storageDict.userModuleAccess, moduleAccess);
      }
    } catch (err) {
      console.error(err);
    }
    this.activeSidebar = [...result];
    if (ValueGuard.againtsNullOrUndefined(initialGroup)) {
      this.activeSidebar = [...this.activeSidebar, ...initialGroup];
    }
  }

  private async loadCustomAdminMenu() {
    const customAdminMenu = sidebarStaticMenuCustomAdmin.filter(
      (item) => item.groupChildren.length > 0
    );
    await this.loadStaffMenu(customAdminMenu);

    // FIXME: Need code improvement on how we add userModuleAccess to the storage.
    from(sidebarStaticMenuCustomAdmin)
      .pipe(
        map((group) => group.groupChildren.map((child) => child.link.replace(/^\//, ''))),
        concatMap((modules) =>
          this.storageService
            .getItemSync(storageDict.userModuleAccess)
            .pipe(
              switchMap((data) =>
                this.storageService.setItemSync(storageDict.userModuleAccess, [...data, ...modules])
              )
            )
        )
      )
      .subscribe();
  }

  /**
   * Filter superadmin menu regarding clinic's price type (klinik/rs/rsp/etc.).
   */
  private async loadSuperadminMenu(filtered = true) {
    this.isLoading$.next(true);
    const clinic = await this.clinicEnv.getClinicAsync();
    const clinicType = clinic.typePrice || 'rs';

    const referenceNavigasi = await this.referenceNavigasiDataService
      .getReferenceNavigasiAll()
      .pipe(map((res) => res.filter((val) => !!val.url)))
      .toPromise();

    const superadminMenu = sidebarStaticMenu.filter((item) => item.groupChildren.length > 0);

    if (filtered === false) {
      this.activeSidebar = superadminMenu;
      return;
    }

    const mappedMenu = superadminMenu.map((menu: MenuGroup) => {
      const gchild = menu.groupChildren
        .map((parent: MenuParent) => {
          let filteredChildren: MenuChild[];
          if (parent.children && parent.children.length > 0) {
            filteredChildren = parent.children
              .filter((child) => {
                if (child.children && child.children.length > 0) {
                  child.children = child.children.filter((child2) => {
                    const compare = referenceNavigasi.find((rn) => rn.url === child2.link);
                    if (!compare || !compare.tipeKlinik) {
                      return true;
                    }
                    const navTypeClinic = compare.tipeKlinik.trim().replace(/\s/g, '').split(',');
                    return navTypeClinic.some(
                      (val) => val.toLowerCase() === clinicType.toLowerCase()
                    );
                  });
                  return true;
                } else {
                  const compare = referenceNavigasi.find((rn) => rn.url === child.link);
                  if (!compare || !compare.tipeKlinik) {
                    return true;
                  }
                  const navTypeClinic = compare.tipeKlinik.trim().replace(/\s/g, '').split(',');
                  return navTypeClinic.some(
                    (val) => val.toLowerCase() === clinicType.toLowerCase()
                  );
                }
              })
              .filter((child) => {
                if (child.excludeClinicIds && child.excludeClinicIds.includes(clinic.id)) {
                  return false;
                }
                if (!child.clinicIds || child.clinicIds.length === 0) {
                  return true;
                }
                return child.clinicIds.some((val) => val === clinic.id);
              });
          } else {
            const compare = referenceNavigasi.find((rn) => rn.url === parent.link);
            if (!compare || !compare.tipeKlinik) {
              return { ...parent };
            }
            const navTypeClinic = compare.tipeKlinik.trim().replace(/\s/g, '').split(',');
            if (navTypeClinic.some((val) => val.toLowerCase() === clinicType.toLowerCase())) {
              return { ...parent };
            } else {
              return null;
            }
          }
          return {
            ...parent,
            children: filteredChildren ? [...filteredChildren] : undefined,
          };
        })
        .filter((val) => !!val);
      return {
        ...menu,
        groupChildren: [...gchild],
      };
    });
    this.activeSidebar = mappedMenu;
  }

  private async loadBuildForms() {
    const capitalize = (text: string) =>
      text.replace(/(^\w|\s\w)(\S*)/g, (m0, m1, m2) => m1.toUpperCase() + m2.toLowerCase());
    const bfQuery = {};
    if (
      this.karyawan?.idInstalasi !== '' &&
      ValueGuard.againtsNullOrUndefined(this.karyawan?.idInstalasi)
    ) {
      bfQuery['idInstallasi'] = this.karyawan.idInstalasi;
    }

    if (
      this.karyawan?.idRuang !== '' &&
      ValueGuard.againtsNullOrUndefined(this.karyawan?.idRuang)
    ) {
      bfQuery['idRuang'] = this.karyawan.idRuang;
    }

    let buildForms: BuildForm[] = await this.buildFormDataService
      .getAll(bfQuery)
      .pipe(
        timeout(15000),
        catchError(() => of([] as BuildForm[]))
      )
      .toPromise();

    buildForms = buildForms
      .filter((item) => !!item.namaTable)
      .map((item) => {
        return {
          ...item,
          type: item.type ? item.type : 'Lain-Lain',
          navigation: {
            head: item.navigation.head || 'Lain-Lain',
            child: item.navigation.child || 'Lain-Lain',
          },
        };
      });

    if (buildForms.length > 0) {
      const uniqueNavHeads = sortBy([...new Set(buildForms.map((val) => val.navigation.head))]);

      // Move 'Lain-Lain' to the latest.
      uniqueNavHeads.push(uniqueNavHeads.splice(uniqueNavHeads.indexOf('Lain-Lain'), 1)[0]);
      let navHeads: MenuParent[] = uniqueNavHeads.map((nav) => {
        const grouped: { [key: string]: BuildForm[] } = groupBy(
          buildForms.filter((bf) => bf.navigation.head === nav),
          'navigation.child'
        );
        return {
          name: nav,
          icon: './assets/media/svg/icons/Periksa/sarana-prasarana-icon.svg',
          children: sortBy(Object.keys(grouped)).map((key: string) => {
            return {
              name: capitalize(key),
              children: grouped[key].map((item) => {
                return {
                  name: item.namaForm,
                  link: `/formulir/build-form-viewer/${item.id}`,
                  tags: ['build-form'],
                } as MenuChild;
              }),
            };
          }),
        } as MenuParent;
      });
      if (navHeads.length > 0) {
        navHeads = [
          ...navHeads,
          {
            single: true,
            name: 'Grafik Formulir',
            link: `/formulir/build-form-grapher`,
            icon: './assets/media/svg/icons/Periksa/dashboard-icon.svg',
          },
          {
            single: true,
            name: 'Laporan Formulir',
            link: `/formulir/build-form-reporting`,
            icon: './assets/media/svg/icons/Periksa/dashboard-icon.svg',
          },
        ];
        const formulirSection: MenuGroup = {
          name: 'Formulir',
          groupChildren: navHeads,
        };
        this.activeSidebar = [...this.activeSidebar, formulirSection];
      }
      // Workaround for the aside component calls cdr.detectChanges(), to fix build form sometimes not showing.
      this.sidebarUpdate$.next();
    }
  }

  /** Hardcoded survey menu, open access to all staff and logins. */
  private loadSurveyMenu() {
    this.activeSidebar = [
      ...this.activeSidebar,
      {
        name: 'Survei',
        groupChildren: [
          {
            name: 'Survei',
            icon: './assets/media/svg/icons/Periksa/sarana-prasarana-icon.svg',
            link: '/survey',
            children: [
              {
                name: `Laporan Insiden ${
                  this.clinicEnv.clinicId === 1 || this.clinicEnv.clinicId === 378
                    ? 'Internal'
                    : 'Keselamatan'
                }`,
                link: '/survey/laporan-insiden-keselamatan',
              },
              {
                name: 'Survei Budaya',
                link: '/survey/survey-budaya-baru',
              },
              {
                name: 'Survei Kepuasan Masyarakat',
                link: '/survey/survey-kepuasan-masyarakat',
              },
              {
                name: 'Survei Kepuasan Pasien Terhadap Pelayanan',
                link: '/survey/survey-kp-pelayanan-baru',
              },
              {
                name: 'Survei Kepuasan Pasien Pelanggan',
                link: '/survey/survey-kepuasan-pasien-pelanggan',
              },
              {
                name: 'Manage Lapor Insiden Budaya Keselamatan',
                link: '/survey/lapor-insiden-budaya-keselamatan',
              },
              {
                name: 'Laporan Insiden Budaya Keselamatan',
                link: '/survey/laporan-insiden-budaya-keselamatan',
              },
              {
                name: 'Survei Persepsi Anti Korupsi',
                link: '/survey/survey-persepsi-anti-korupsi',
              },
            ],
          },
        ],
      },
    ];
    this.sidebarUpdate$.next();
  }

  /** Hardcoded manage laporan menu, open access to all staff and logins. */
  private loadManageLaporanLadokgi() {
    const sidebarItem = this.activeSidebar.find((val) => val.name === 'Periksa - Laporan');

    if (!sidebarItem) {
      return;
    }

    let manageLaporanGroup = sidebarItem.groupChildren.find(
      (child) => child.name === 'Manage Laporan'
    );

    if (!manageLaporanGroup) {
      manageLaporanGroup = {
        name: 'Manage Laporan',
        icon: './assets/media/svg/icons/Periksa/laporan-medrec-icon.svg',
        link: '/laporan',
        children: [],
      };
      sidebarItem.groupChildren.push(manageLaporanGroup);
    }

    manageLaporanGroup.children.unshift(
      // {
      //   name: 'Laporan Harian Rawat Inap Prajurit TNI AL PNS Keluarga Dan Purnawirawan',
      //   link: '/laporan/laporan-harian-rawat-inap-prajurit-tni-al-pns-keluarga-dan-purnawirawan',
      // },
      // {
      //   name: '1.C1 Laporan Data Umum Kesehatan Jumlah Pengunjung Berobat Di RSGM Ladokgi REM',
      //   link: '/laporan/laporan-data-umum-kesehatan-jumlah-pengunjung-berobat',
      // },
      // {
      //   name: '1.C2 Laporan Data Umum Kesehatan Kunjungan Per Klinik',
      //   link: '/laporan/laporan-data-umum-kesehatan',
      // },
      // {
      //   name: '2. Indikator Pelayanan Rumah Sakit',
      //   link: '/laporan/indikator-pelayanan-rumah-sakit',
      // },
      // {
      //   name: '2. Laporan Penyakit dan Jumlah Penderita Rawat Jalan',
      //   link: '/laporan/laporan-penyakit-dan-jumlah-penderita-rawat-jalan',
      // },
      // ------------------------------------------------------------------------------
      // {
      //   name: '3. Rekapitulasi Kegiatan Pelayanan',
      //   link: '/laporan/rekapitulasi-kegiatan-pelayanan',
      // },
      // {
      //   name: '3. Laporan Tindakan Operasi',
      //   link: '/laporan/laporan-tindakan-operasi',
      // },
      // {
      //   name: '3.A Rekapitulasi Kegiatan Pelayanan',
      //   link: '/laporan/laporan-rekapitulasi-kegiatan-pelayanan',
      // },
      // {
      //   name: '3.B Kunjungan Rawat Jalan',
      //   link: '/laporan/kunjungan-rawat-jalan-b',
      // },
      // ------------------------------------------------------------------------------
      // { (dihapus)
      //   name: '3.C1 Pelayanan Rawat Darurat dan Kegiatan',
      //   link: '/laporan/kunjungan-rawat-jalan-a',
      // },
      // ------------------------------------------------------------------------------
      // {
      //   name: '3.C2 Rekapitulasi Kegiatan Pelayanan Rawat Darurat',
      //   link: '/laporan/kunjungan-rawat-jalan-c',
      // },

      // {
      //   name: '3.D1 Rekapitulasi Kegiatan Pelayanan Inap',
      //   link: '/laporan/rekapitulasi-kegiatan-pelayanan-inap',
      // },
      // {
      //   name: '3.D2 Kegiatan Pelayanan Rawat Inap',
      //   link: '/laporan/kegiatan-pelayanan-rawat-inap',
      // },
      // {
      //   name: '3.E Kegiatan Kesehatan Gigi Dan Mulut',
      //   link: '/laporan/kegiatan-kesehatan-gigi-dan-mulut',
      // },

      // { (DIHAPUS)
      //   name: '3.G Kegiatan Radiologi',
      //   link: '/laporan/kegiatan-radiologi',
      // },

      // {
      //   name: '3.H Kegiatan Pembedahan',
      //   link: '/laporan/kegiatan-pembedahan',
      // },
      // {
      //   name: '3.J Pemeriksaan Laboratorium dan Kegiatan',
      //   link: '/laporan/kegiatan-laboratorium-gigi',
      // },

      // { (DIHAPUS)
      //   name: '4. Laporan Laboratorium Patologi',
      //   link: '/laporan/laporan-laboratorium-patologi',
      // },
      // {
      //   name: '4. Kegiatan Rujukan',
      //   link: '/laporan/kegiatan-rujukan',
      // },
      // {
      //   name: '5. Data Penyebab Kematian',
      //   link: '/laporan/data-penyebab-kematian',
      // },
      // {
      //   name: '5. Laporan Radiologi Reguler Dan Eksekutif',
      //   link: '/laporan/laporan-pemeriksaan-radiologi',
      // },
      // -------------------------------------------------------------
      // {
      //   name: '6. Laporan Banyaknya Pengobatan dan Perawatan',
      //   link: '/laporan/laporan-banyaknya-pengobatan-dan-perawatan',
      // },
      // {
      //   name: '6.A Data Keadaan Morbiditas Pasien Rawat Inap',
      //   link: '/laporan/laporan-data-keadaan-morbiditas-pasien-ranap',
      // },
      // {
      //   name: '6.B Data Keadaan Morbiditas Pasien Rawat Inap Penyebab Kecelakaan',
      //   link: '/laporan/laporan-data-keadaan-morbiditas-pasien-ranap-sebab-kecelakaan',
      // },
      // {
      //   name: '6.C Data Keadaan Morbiditas Pasien Rawat Jalan',
      //   link: '/laporan/laporan-data-keadaan-morbiditas-pasien-rajal',
      // },
      // {
      //   name: '6.D Data Keadaan Morbiditas Pasien Rawat Jalan Penyebab Kecelakaan',
      //   link: '/laporan/laporan-data-keadaan-morbiditas-pasien-rajal-sebab-kecelakaan',
      // },
      // ----------------------------------------------------
      // {
      //   name: '7. Laporan Laboratorium Gigi Reguler',
      //   link: '/laporan/laporan-laboratorium-gigi-reguler',
      // },
      // ---------------------------------------------------------
      {
        name: '7.A Daftar 10 Besar Penyakit Rawat Inap',
        link: '/laporan/laporan-daftar-sepuluh-besar-penyakit-ranap',
      },
      {
        name: '7.B Daftar 10 Besar Penyakit Rawat Jalan',
        link: '/laporan/laporan-daftar-sepuluh-besar-penyakit-rajal',
      },
      // -----------------------------------------------------------
      // {
      //   name: '8. Kunjungan Klinik Pasien Baru dan Lama',
      //   link: '/laporan/kunjungan-klinik-pasien-baru-dan-lama',
      // },
      // {
      //   name: '9. Laporan Rujukan Ladokgi',
      //   link: '/laporan/laporan-rujukan-ladokgi',
      // },
      // -------------------------------------------------------------
      // {
      //   name: '11. Laporan Laboratorium Gigi Eksekutif',
      //   link: '/laporan/laporan-laboratorium-gigi-eksekutif',
      // },
      // {
      //   name: '12. Laporan Pati Eksekutif ',
      //   link: '/laporan/laporan-pati-eksekutif',
      // },
      // {
      //   name: '13. Yanmasum ',
      //   link: '/laporan/laporan-yanmasum',
      // },
      {
        name: 'Laporan Tindakan Klinik',
        link: '/laporan/laporan-tindakan-klinik',
      }
    );

    this.sidebarUpdate$.next();
  }

  private loadManageLaporanDiskesal() {
    const sidebarItem = this.activeSidebar.find((val) => val.name === 'Periksa - Laporan');

    if (!sidebarItem) {
      return;
    }

    let manageLaporanGroup = sidebarItem.groupChildren.find(
      (child) => child.name === 'Laporan Diskesal'
    );

    if (!manageLaporanGroup) {
      manageLaporanGroup = {
        name: 'Laporan Diskesal',
        icon: './assets/media/svg/icons/Periksa/laporan-medrec-icon.svg',
        link: '/laporan',
        children: [],
      };
      sidebarItem.groupChildren.push(manageLaporanGroup);
    }

    manageLaporanGroup.children.unshift(
      {
        name: 'Laporan 3.a Data Pengunjung',
        link: '/laporan/laporan3a-data-pengunjung',
        clinicIds: [20, 31],
      },
      {
        name: 'Laporan 3.b Kunjungan Rawat Jalan',
        link: '/laporan/laporan3b-kunjungan-rawat-jalan',
        clinicIds: [20, 31],
      },
      {
        name: 'Laporan 3.e Kegiatan Kesehatan Gigi dan Mulut',
        link: '/laporan/oral-health-report',
        clinicIds: [20, 31],
      },
      {
        name: 'Laporan 3.j Pemeriksaan Laboratorium dan Kegiatan',
        link: 'laporan/laboratory-examinations-report',
        clinicIds: [20, 31],
      },
      {
        name: 'Laporan 3.h Kegiatan Pembedahan',
        link: 'laporan/laporan3h-kegiatan-pembedahan',
        clinicIds: [20, 31],
      },
      {
        name: 'Laporan 3.i Kegiatan Radiologi',
        link: 'laporan/laporan3i-kegiatan-radiologi',
        clinicIds: [20, 31],
      },
      {
        name: 'Laporan 3.c.2 Kegiatan Pelayanan Rawat Darurat',
        link: 'laporan/laporan3c2-kegiatan-pelayanan-rawat-darurat',
        clinicIds: [20, 31],
      },
      {
        name: 'Laporan 3.d.1 Rekapitulasi Kegiatan Pelayanan Rawat Inap',
        link: 'laporan/laporan3d1-rekapitulasi-kegiatan-pelayanan-rawat-inap',
        clinicIds: [20, 31],
      },
      {
        name: 'Laporan 3.d.2 Laporan Kegiatan Pelayanan Rawat Inap',
        link: 'laporan/laporan-kegiatan-pelayanan-ranap',
        clinicIds: [20, 31],
      }
    );

    this.sidebarUpdate$.next();
  }

  private loadManageLaporanJatiPadang() {
    const sidebarItem = this.activeSidebar.find((val) => val.name === 'Periksa - Laporan');

    if (!sidebarItem) {
      return;
    }

    let manageLaporanGroup = sidebarItem.groupChildren.find(
      (child) => child.name === 'Manage Laporan'
    );

    if (!manageLaporanGroup) {
      manageLaporanGroup = {
        name: 'Manage Laporan',
        icon: './assets/media/svg/icons/Periksa/laporan-medrec-icon.svg',
        link: '/laporan',
        children: [],
      };
      sidebarItem.groupChildren.push(manageLaporanGroup);
    }

    manageLaporanGroup.children.unshift(
      {
        name: 'Laporan Kunjungan Register Rawat Jalan',
        link: '/laporan/laporan-kunjungan-register-rawat-jalan',
      },
      {
        name: 'Laporan Harian',
        link: '/laporan/laporan-harian',
      },
      {
        name: 'Laporan RL Morbiditas dan Mortalitas',
        link: '/laporan/laporan-rl-morbiditas-mortalitas-rawat-inap',
      },
      {
        name: 'Laporan Kesehatan Balita (0-5 Tahun)',
        link: '/laporan/laporan-kesehatan-balita',
      },
      {
        name: 'Laporan HAIS Harian',
        link: '/laporan/laporan-hais-harian',
      },
      {
        name: 'Laporan Analisis Kualitatif Penggunaan Antibiotik',
        link: '/laporan/laporan-analisis-kualitatif-antibiotik-rawat-jalan',
      },
      {
        name: 'Laporan Skrining PTM',
        link: '/laporan/laporan-skrining-ptm',
      }
    );

    this.sidebarUpdate$.next();
  }

  private loadManageLaporanMarinirCilandak() {
    const sidebarItem = this.activeSidebar.find((val) => val.name === 'Periksa - Laporan');

    if (!sidebarItem) {
      return;
    }

    // nyari "Manage Laporan"
    let manageLaporanGroup = sidebarItem.groupChildren.find(
      (child) => child.name === 'Manage Laporan'
    );

    if (!manageLaporanGroup) {
      manageLaporanGroup = {
        name: 'Manage Laporan',
        icon: './assets/media/svg/icons/Periksa/laporan-medrec-icon.svg',
        link: '/laporan',
        children: [],
      };
      sidebarItem.groupChildren.push(manageLaporanGroup);
    }

    // Push laporan
    manageLaporanGroup.children.unshift(
      {
        name: 'Laporan Rekapitulasi Pasien Rawat Inap',
        link: '/laporan/laporan-rekapitulasi-pasien-rawat-inap',
      },
      {
        name: 'Perincian Pasien Masuk/Keluar Rawat Inap',
        link: '/laporan/perincian-pasien-masuk-keluar-rawat-inap',
      }
    );

    // remove dari LMR
    const medicalRecordGroup = sidebarItem.groupChildren.find(
      (child) => child.name === 'Laporan Medical Record'
    );

    if (medicalRecordGroup) {
      medicalRecordGroup.children = medicalRecordGroup.children.filter(
        (item) => item.name !== 'Perincian Pasien Masuk/Keluar Rawat Inap'
      );
    }

    this.sidebarUpdate$.next();
  }

  private loadManageLaporanBenyaminGuluh() {
    const sidebarItem = this.activeSidebar.find((val) => val.name === 'Periksa - Laporan');

    if (!sidebarItem) {
      return;
    }

    let manageLaporanGroup = sidebarItem.groupChildren.find(
      (child) => child.name === 'Manage Laporan'
    );

    if (!manageLaporanGroup) {
      manageLaporanGroup = {
        name: 'Manage Laporan',
        icon: './assets/media/svg/icons/Periksa/laporan-medrec-icon.svg',
        link: '/laporan',
        children: [],
      };
      sidebarItem.groupChildren.push(manageLaporanGroup);
    }

    manageLaporanGroup.children.unshift({
      name: 'Laporan Rekap Kunjungan',
      link: '/laporan/laporan-rekap-kunjungan',
    });
    this.sidebarUpdate$.next();
  }

  private async loadSatuSehatNav() {
    const authSatusehat: { dateMake: string } = await this.settingAuthSatuSehatDataService
      .getAll()
      .pipe(
        timeout(15000),
        catchError(() => of(null))
      )
      .toPromise();

    const bridgingParentIdx = this.activeSidebar
      .map((val) => val.name)
      .indexOf('Periksa - Bridging');
    if (bridgingParentIdx !== -1) {
      const findChildIdx = this.activeSidebar[bridgingParentIdx].groupChildren
        .map((val) => val.name)
        .indexOf('RS Online');
      if (findChildIdx !== -1) {
        this.activeSidebar[bridgingParentIdx].groupChildren.splice(findChildIdx + 1, 0, {
          name: 'SATUSEHAT',
          icon: './assets/media/svg/icons/Periksa/integrasi-icon.svg',
          link: '/satu-sehat',
          children: [],
        });

        const findSatusehatNavIdx = this.activeSidebar[bridgingParentIdx].groupChildren
          .map((val) => val.name)
          .indexOf('SATUSEHAT');
        if (findSatusehatNavIdx !== -1) {
          const satusehatChild =
            this.activeSidebar[bridgingParentIdx].groupChildren[findSatusehatNavIdx].children;

          if (authSatusehat) {
            satusehatChild.push({
              name: 'Log SATUSEHAT Kemenkes',
              link: '/satu-sehat/manage-log-satusehat',
            });
          }

          if (this.clinicEnv.clinicId === 200) {
            satusehatChild.push({
              name: 'Satu Data',
              link: '/satu-sehat/manage-satu-data',
            });
          }
        }
      }
    }
  }

  private loadManageLaporanRsgmUsakti() {
    const sidebarItem = this.activeSidebar.find((val) => val.name === 'Periksa - Laporan');

    if (!sidebarItem) {
      return;
    }

    let manageLaporanGroup = sidebarItem.groupChildren.find(
      (child) => child.name === 'Manage Laporan'
    );

    if (!manageLaporanGroup) {
      manageLaporanGroup = {
        name: 'Manage Laporan',
        icon: './assets/media/svg/icons/Periksa/laporan-medrec-icon.svg',
        link: '/laporan',
        children: [],
      };
      sidebarItem.groupChildren.push(manageLaporanGroup);
    }

    manageLaporanGroup.children.forEach((child) => {
      if (child.link === '/laporan/laporan-kegiatan-radiologi-per-hari') {
        child.name = 'Laporan Kegiatan Radiologi Per Bulan';
        child.link = '/laporan/laporan-kegiatan-radiologi-per-bulan-v2';
      }
      if (child.link === '/laporan/laporan-kegiatan-radiologi-per-bulan') {
        child.name = 'Laporan Kegiatan Radiologi Per Tahun';
        child.link = '/laporan/laporan-kegiatan-radiologi-per-tahun-v2';
      }
      if (child.link === '/laporan/laporan-kegiatan-dokter-dpjp-per-hari') {
        child.name = 'Laporan Kegiatan Dokter Atau DPJP Per Bulan';
        child.link = '/laporan/laporan-kegiatan-dokter-dpjp-per-bulan-v2';
      }
      if (child.link === '/laporan/laporan-kegiatan-dokter-dpjp-per-bulan') {
        child.name = 'Laporan Kegiatan Dokter Atau DPJP Per Tahun';
        child.link = '/laporan/laporan-kegiatan-dokter-dpjp-per-tahun-v2';
      }
      if (child.link === '/laporan/laporan-kegiatan-instalasi-per-hari') {
        child.name = 'Laporan Kegiatan Instalasi Per Bulan';
        child.link = '/laporan/laporan-kegiatan-instalasi-per-bulan-v2';
      }
      if (child.link === '/laporan/laporan-kegiatan-instalasi-per-bulan') {
        child.name = 'Laporan Kegiatan Instalasi Per Tahun';
        child.link = '/laporan/laporan-kegiatan-instalasi-per-tahun-v2';
      }
    });
    this.sidebarUpdate$.next();
  }

  private async loadOutpatientQueueMonitorSetting() {
    const sidebarItem = this.activeSidebar.find((val) => val.name === 'Periksa - TV Media');
    if (!sidebarItem) return;

    let tvInformasiMediaGroup = sidebarItem.groupChildren.find(
      (child) => child.name === 'TV Informasi Media'
    );
    if (!tvInformasiMediaGroup) return;

    const antrianPoliMonitorIndex = tvInformasiMediaGroup.children.findIndex(
      (timg) => timg.name === 'TV Display Antrian Poli'
    );
    if (!antrianPoliMonitorIndex || antrianPoliMonitorIndex === -1) return;

    const settingMonitor: SettingMonitorOutpatientQueue[] =
      await this.settingMonitorOutpatientQueueDataService
        .getAll()
        .pipe(
          timeout(15000),
          catchError(() => of(null))
        )
        .toPromise();
    if (!settingMonitor || settingMonitor?.length === 0) return;

    let outpatientQueueMonitor = settingMonitor.map((sm) => {
      const nav = {
        link: `/tv-media/outpatient-queue/${sm.settingNumber}`,
        name: `TV Display Antrian Poli ${sm.settingNumber}`,
        newTab: true,
      };
      const isSettingExist = tvInformasiMediaGroup.children.findIndex(
        (timg) => timg.name === nav.name
      );
      if (isSettingExist === -1) {
        return nav;
      }
    });
    outpatientQueueMonitor = compact(outpatientQueueMonitor);
    if (tvInformasiMediaGroup) {
      tvInformasiMediaGroup.children.splice(
        antrianPoliMonitorIndex + 1,
        0,
        ...outpatientQueueMonitor
      );
    }
  }

  private filterMenuByClinic(menuGroups: MenuGroup[], clinicId: number): MenuGroup[] {
    if (!menuGroups) return [];
    const filterChild = (child: MenuChild): MenuChild | null => {
      if (child.excludeClinicIds && child.excludeClinicIds.includes(clinicId)) {
        return null;
      }
      if (child.clinicIds && child.clinicIds.length > 0 && !child.clinicIds.includes(clinicId)) {
        return null;
      }
      if (child.children && child.children.length > 0) {
        const filteredSubChildren = child.children
          .map((subChild) => filterChild(subChild))
          .filter((subChild) => !!subChild);
        return {
          ...child,
          children: filteredSubChildren,
        };
      }
      return child;
    };

    return menuGroups.map((group) => {
      if (!group.groupChildren) return group;
      const filteredParents = group.groupChildren.map((parent) => {
        if (!parent.children) return parent;
        const filteredChildren = parent.children
          .map((child) => filterChild(child))
          .filter((child) => !!child);
        return {
          ...parent,
          children: filteredChildren,
        };
      });
      return {
        ...group,
        groupChildren: filteredParents,
      };
    });
  }

  private async initSidebar() {
    this.karyawan = await this.storageService.getItem(storageDict.karyawan);
    if (this.karyawan) {
      this.isLoading$.next(true);
      const staffCategory = this.karyawan.kategoriKaryawan?.id || 2;

      if (staffCategory === 1) {
        // Superadmin, kategoriKaryawan == 1
        try {
          await this.loadSuperadminMenu();
        } catch (err) {
          this.activeSidebar = sidebarStaticMenu.filter((item) => item.groupChildren.length > 0);
        }
      } else if (staffCategory === 31) {
        // Custom Admin
        await this.loadCustomAdminMenu();
      } else if (staffCategory === 44) {
        // Periksa Superadmin
        await this.loadSuperadminMenu(false);
      } else {
        // Other staffs, get menu from navigation table.
        await this.loadStaffMenu();
      }

      try {
        // ? workaround to ensure clinic is set before load build-forms.
        await this.clinicEnv.getClinicIdAsync();
        await this.loadBuildForms();
      } catch (err) {
        // Do nothing
        console.warn('Failed to fetch build forms.', err);
      }
      if (this.clinicEnv.clinicType === 'rs' || this.clinicEnv.clinicType === 'rsp') {
        this.loadSurveyMenu();
      }

      if (this.clinicEnv.clinicId === 7) {
        this.loadManageLaporanRsgmUsakti();
      }

      if (this.clinicEnv.clinicId === 20) {
        this.loadManageLaporanLadokgi();
      }

      if (this.clinicEnv.clinicId === 641 || this.clinicEnv.clinicId === 1) {
        this.loadManageLaporanJatiPadang();
      }

      if (this.clinicEnv.clinicId === 173) {
        this.loadManageLaporanMarinirCilandak();
      }

      if (this.clinicEnv.clinicId === 131) {
        this.loadManageLaporanBenyaminGuluh();
      }

      // add tv display antrian poli with setting
      this.loadOutpatientQueueMonitorSetting();

      try {
        const settingResep = await this.settingResepDataService.getSetting().toPromise();
        if (settingResep?.statusBmhp == 1) {
          this.loadPharmacyBMHP();
        }
      } catch (err) {
        console.warn('Failed to fetch setting resep.', err);
      }

      try {
        await this.loadSatuSehatNav();
      } catch (err) {
        console.warn('Failed to fetch SATUSEHAT settings.', err);
      }

      if (this.clinicEnv.clinicId) {
        this.activeSidebar = this.filterMenuByClinic(this.activeSidebar, this.clinicEnv.clinicId);
      }

      this.isLoading$.next(false);

      this.isInitialized = true;
    }
  }

  private initAuthListener() {
    if (this.isInitialized === true) return;
    this.authService.authState$.subscribe((res) => {
      if (res === true) {
        this.activeSidebar = [];
        this.initSidebar();
      }
    });
    this.isInitialized = true;
  }

  public getSidebar() {
    return this.activeSidebar;
  }

  public async refresh() {
    this.activeSidebar = [];
    await this.initSidebar();
  }

  private loadPharmacyBMHP() {
    this.activeSidebar.forEach((group) => {
      if (group.groupChildren) {
        const farmasiGroup = group.groupChildren.find((child) => child.name === 'Farmasi');
        if (farmasiGroup && farmasiGroup.children) {
          const menuTulisResep = farmasiGroup.children.find(
            (c) => c.link === '/farmasi/tulis-resep-digital/0' || c.name === 'Tulis Resep Digital'
          );
          if (menuTulisResep) {
            menuTulisResep.name = 'Tulis Resep Digital dan BMHP';
          }

          const menuAntrianResep = farmasiGroup.children.find(
            (c) => c.link === '/farmasi/antrian-resep-pasien/0' || c.name === 'Antrian Resep Pasien'
          );
          if (menuAntrianResep) {
            menuAntrianResep.name = 'Antrian Resep dan BMHP Digital';
          }

          const menuRiwayatResep = farmasiGroup.children.find(
            (c) =>
              c.link === '/farmasi/riwayat-resep-digital/0' || c.name === 'Riwayat Resep Digital'
          );
          if (menuRiwayatResep) {
            menuRiwayatResep.name = 'Riwayat Resep Digital dan BMHP';
          }
        }
      }
    });
    this.sidebarUpdate$.next();
  }

  /** Fetch flattened menu with links as an array. */
  public async getAvailableMenus(refetch = false): Promise<any[]> {
    return new Promise(async (resolve) => {
      const result = [];

      const pushResult = (item: MenuChild, icon: string) => {
        result.push({
          ...item,
          icon,
        });
      };

      if (refetch && this.isLoading$.value === false) {
        await this.initSidebar();
      }
      this.activeSidebar.forEach((group) => {
        if (group.groupChildren) {
          group.groupChildren.forEach((parent) => {
            if (parent.children && parent.children.length > 0) {
              parent.children.forEach((item) => {
                if (item.children && item.children.length > 0) {
                  item.children.forEach((itemChild) => {
                    pushResult(itemChild, parent.icon);
                  });
                } else {
                  pushResult(item, parent.icon);
                }
              });
            } else {
              result.push(parent);
            }
          });
        }
      });
      resolve(result);
    });
  }
}
